<template>
  <div
    ref="containerRef"
    class="relative flex flex-col shrink-0 bg-card border-r border-border font-ds"
    :style="containerStyles"
  >
    <button
      v-if="isCollapsed"
      type="button"
      :title="`Show ${caption}`"
      class="absolute inset-0 flex flex-col items-center gap-3 pt-3 text-muted-foreground hover:text-foreground hover:bg-background cursor-pointer"
      @click="resetWidth"
    >
      <ListTreeIcon
        class="size-4"
        aria-hidden="true"
      />
      <span class="[writing-mode:vertical-rl] text-[11px] font-bold uppercase tracking-ds-wide">
        {{ caption }}
      </span>
    </button>
    <slot
      v-else
      :collapse="handleCollapse"
    />
    <div
      ref="resizerRef"
      class="absolute top-0 bottom-0 -right-0.75 w-1.25 cursor-col-resize z-10"
      :class="isCollapsed ? 'pointer-events-none opacity-0' : (isDragging ? 'opacity-100' : 'opacity-0 hover:opacity-100')"
    >
      <div class="h-full w-px mx-auto bg-ring" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, useTemplateRef, watch } from 'vue'
import {
  useDebounceFn,
  useDraggable,
  useElementBounding,
  useLocalStorage,
  useResizeObserver,
} from '@vueuse/core'
import { ListTreeIcon } from '@lucide/vue'

import { useHotKeys } from '@/plugins/hotKeys'

import { useUserSettingsStore } from '@/store/user/settings'

const START_WIDTH = 350
const MIN_WIDTH = 240
const MIN_CONTENT_WIDTH = 370

type Props = {
  caption?: string
}

const { caption = 'Family structure' } = defineProps<Props>()

const userSettingsStore = useUserSettingsStore()

const containerRef = useTemplateRef<HTMLDivElement>('containerRef')
const resizerRef = useTemplateRef('resizerRef')

const containerWidth = ref(MIN_WIDTH)

const isInited = ref(false)

const isTreeOpened = useLocalStorage<boolean>('TREE_OPENED', true)

const { left, width } = useElementBounding(containerRef)
const { isDragging, x } = useDraggable(resizerRef, {
  axis: 'x',
})

const storageWidth = computed({
  get() {
    return userSettingsStore.getAssetTreeWidth || START_WIDTH
  },
  set(value) {
    userSettingsStore.setAssetTreeWidth(value)
  },
})

const isCollapsed = computed(() => containerWidth.value < MIN_WIDTH)
const maxWidth = computed(() => document.body.clientWidth - MIN_CONTENT_WIDTH)

const containerStyles = computed(() => ({
  width: isCollapsed.value ? '44px' : `${containerWidth.value}px`,
}))

const handleToggle = () => {
  isCollapsed.value ? resetWidth() : handleCollapse()
}

const handleCollapse = () => {
  containerWidth.value = 0
}

const initWidth = () => {
  containerWidth.value = isTreeOpened.value ? storageWidth.value : 0
}

const resetWidth = () => {
  containerWidth.value = storageWidth.value
}

const updateStorageWidth = useDebounceFn((value: number) => {
  storageWidth.value = value < MIN_WIDTH ? MIN_WIDTH + 10 : value
}, 500)

const updateWidth = () => {
  let newWidth
  const offset = x.value > 0 ? x.value : left.value + width.value
  if (offset > maxWidth.value) {
    newWidth = maxWidth.value - left.value
  } else {
    newWidth = offset - left.value
  }
  containerWidth.value = newWidth
  updateStorageWidth(newWidth)
}

const updateWidthDebounced = useDebounceFn(updateWidth, 300)

useResizeObserver(document.body, () => {
  if (isCollapsed.value) return
  if (!isInited.value) {
    isInited.value = true
    return
  }
  updateWidthDebounced()
})

watch(isDragging, value => {
  if (value) {
    document.body.classList.add('select-none', 'cursor-col-resize')
  } else {
    document.body.classList.remove('select-none', 'cursor-col-resize')
  }
})

watch(x, () => {
  if (!isDragging.value) return
  updateWidth()
})

watch(isCollapsed, value => {
  isTreeOpened.value = !value
})

onBeforeMount(initWidth)

useHotKeys('tree', handleToggle)
</script>
